import { HTMLAttributes, useState } from 'react';
import { Autocomplete, Box, Paper, Popper, SxProps, TextField, Theme } from '@mui/material';
import { PopperProps } from '@mui/material/Popper/BasePopper.types';
import { Controller, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { getContentLanguage, Group, Tag } from '@/core';

const CenterPopper = (props: PopperProps) => {
  return (
    <Popper
      {...props}
      placement="bottom"
      style={{
        width: '100%',
        left: '50% !important',
        transform: 'translateX(-50%) !important',
        position: 'fixed',
      }}
      modifiers={[
        {
          name: 'flip',
          enabled: false,
        },
        {
          name: 'preventOverflow',
          enabled: false,
        },
      ]}
    />
  );
};

const CustomPaper = (props: HTMLAttributes<HTMLDivElement>) => {
  return (
    <Paper
      {...props}
      sx={{
        li: { fontSize: '16px' },
      }}
    />
  );
};

type FormAutocompleteMultiselectControllerProps = {
  label: string;
  controllerName: string;
  options: Group[] | Tag[];
  autocompleteId?: string;
  placeholder: string;
  limitTags?: number;
  fieldOnBlur?: () => void;
  sx?: SxProps<Theme>;
};

export const FormAutocompleteMultiselectController = ({
  label,
  controllerName,
  options,
  autocompleteId,
  placeholder,
  limitTags = 1,
  fieldOnBlur,
  sx,
}: FormAutocompleteMultiselectControllerProps) => {
  const { i18n } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const { control } = useFormContext();
  return (
    <Controller
      name={controllerName}
      control={control}
      render={({ field: { onChange, onBlur, value } }) => (
        <Box sx={{ position: 'relative', height: '56px' }}>
          <Box
            sx={{
              position: 'absolute',
              width: '100%',
              zIndex: 2,
              '.MuiAutocomplete-tag': {
                maxWidth: isOpen ? 'calc(100% - 10px)' : '155px !important',
              },
              '.MuiInputBase-root': { minHeight: '56px' },
            }}
          >
            <Autocomplete
              filterSelectedOptions
              disablePortal
              multiple
              limitTags={limitTags}
              id={autocompleteId}
              options={options}
              onBlur={() => {
                onBlur();
                fieldOnBlur?.();
              }}
              onOpen={() => setIsOpen(true)}
              onClose={() => setIsOpen(false)}
              getOptionLabel={(option) => option.name[getContentLanguage(i18n.language)]}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              value={value ?? []}
              PaperComponent={CustomPaper}
              PopperComponent={CenterPopper}
              onChange={(_event, newValue) => {
                onChange(newValue);
                // on 'x' (delete) tag click, update api immediately to ensure that the change is saved
                if (newValue.length < (value?.length || 0)) {
                  fieldOnBlur?.();
                }
              }}
              renderInput={(params) => (
                <TextField {...params} label={label} placeholder={placeholder} />
              )}
              sx={sx}
            />
          </Box>
        </Box>
      )}
    />
  );
};
